import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, Building2, CreditCard, Shield, LogOut, Loader2, Search, Clock, CheckCircle, RefreshCw, Plus, Minus, Trash2, DollarSign, Save, Edit2, UserPlus, Globe } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MetricCard = ({ title, value, icon: Icon, color }) => (
  <Card>
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className={`text-3xl font-bold ${color}`}>{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-full bg-muted flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const StatusBadge = ({ status }) => {
  const styles = {
    trial: "bg-yellow-100 text-yellow-800",
    active: "bg-green-100 text-green-800",
    expired: "bg-red-100 text-red-800",
    canceled: "bg-gray-100 text-gray-800",
    unrestricted: "bg-purple-100 text-purple-800"
  };
  const labels = {
    trial: "trial",
    active: "active",
    expired: "expired",
    canceled: "canceled",
    unrestricted: "Admin"
  };
  return <Badge className={styles[status] || styles.trial}>{labels[status] || status}</Badge>;
};

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const { t, language, setLanguage, supportedLanguages, isRTL } = useLanguage();
  const [user, setUser] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editData, setEditData] = useState({});
  const [showTrialDialog, setShowTrialDialog] = useState(false);
  const [trialDaysInput, setTrialDaysInput] = useState(10);
  const [trialAction, setTrialAction] = useState("add"); // "add" or "set"
  
  // Pricing state
  const [pricingPlans, setPricingPlans] = useState({});
  const [editingPlan, setEditingPlan] = useState(null);
  const [editedPlanData, setEditedPlanData] = useState({});
  const [pricingLoading, setPricingLoading] = useState(false);
  
  // Confirmation dialog state
  const [confirmAction, setConfirmAction] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
const [deleteMode, setDeleteMode] = useState("soft");
const [deleteConfirmText, setDeleteConfirmText] = useState("");
const [showDeleteDialog, setShowDeleteDialog] = useState(false);
const [deleting, setDeleting] = useState(false);
  // Custom Enterprise Pricing state
  const [showEnterpriseDialog, setShowEnterpriseDialog] = useState(false);
  const [enterpriseData, setEnterpriseData] = useState({ price: 0, max_employees: 200, create_stripe_price: true });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userRes = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include' });
        if (!userRes.ok) { navigate('/'); return; }
        const userData = await userRes.json();
        
        if (!userData.is_admin && userData.email !== "ebrahemgouyrr@gmail.com") {
          toast.error("Admin access required");
          navigate('/');
          return;
        }
        setUser(userData);

        const [metricsRes, usersRes, companiesRes, txnRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/metrics`, { credentials: 'include' }),
          fetch(`${API_URL}/api/admin/users`, { credentials: 'include' }),
          fetch(`${API_URL}/api/admin/companies`, { credentials: 'include' }),
          fetch(`${API_URL}/api/admin/transactions`, { credentials: 'include' })
        ]);

        if (metricsRes.ok) setMetrics(await metricsRes.json());
        if (usersRes.ok) setUsers(await usersRes.json());
        if (companiesRes.ok) setCompanies(await companiesRes.json());
        if (txnRes.ok) setTransactions(await txnRes.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [navigate]);

  const handleUpdateSubscription = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/company/${selectedCompany.company_id}/subscription`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editData)
      });
      if (res.ok) {
        toast.success("Company updated");
        setShowEditDialog(false);
        // Refresh companies
        const companiesRes = await fetch(`${API_URL}/api/admin/companies`, { credentials: 'include' });
        if (companiesRes.ok) setCompanies(await companiesRes.json());
      }
    } catch (e) { toast.error("Failed to update"); }
  };

  const handleExtendTrial = async (companyId, days) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/company/${companyId}/extend-trial`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ extra_days: days })
      });
      if (res.ok) {
        toast.success(`Trial extended by ${days} days`);
        refreshCompanies();
      }
    } catch (e) { toast.error("Failed to extend trial"); }
  };

  const handleSetTrialDays = async (companyId, days) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/company/${companyId}/subscription`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ trial_days: days, subscription_status: "trial" })
      });
      if (res.ok) {
        toast.success(`Trial set to ${days} days`);
        refreshCompanies();
        setShowTrialDialog(false);
      }
    } catch (e) { toast.error("Failed to set trial"); }
  };

  const refreshCompanies = async () => {
    const companiesRes = await fetch(`${API_URL}/api/admin/companies`, { credentials: 'include' });
    if (companiesRes.ok) setCompanies(await companiesRes.json());
  };

  const refreshTransactions = async () => {
    const txnRes = await fetch(`${API_URL}/api/admin/transactions`, { credentials: 'include' });
    if (txnRes.ok) setTransactions(await txnRes.json());
  };

  const handleUpdateTransaction = async (transactionId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/transaction/${transactionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ payment_status: newStatus })
      });
      if (res.ok) {
        toast.success(`Transaction updated to ${newStatus}`);
        refreshTransactions();
        if (newStatus === "paid") refreshCompanies(); // Also refresh companies if marked as paid
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to update transaction");
      }
    } catch (e) { toast.error("Failed to update transaction"); }
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (!confirm("Are you sure you want to delete this transaction?")) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/transaction/${transactionId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        toast.success("Transaction deleted");
        refreshTransactions();
      }
    } catch (e) { toast.error("Failed to delete transaction"); }
  };

  const handleClearAllShifts = async (companyId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/company/${companyId}/shifts/clear`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) toast.success("All shifts cleared");
    } catch (e) { toast.error("Failed to clear shifts"); }
  };

  const handleResetTrial = async (companyId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/company/${companyId}/reset-trial`, {
        method: 'PUT',
        credentials: 'include'
      });
      if (res.ok) {
        toast.success("Trial reset to 10 days");
        refreshCompanies();
      }
    } catch (e) { toast.error("Failed to reset trial"); }
  };

  const handleToggleMultipleEmployers = async (companyId, currentValue) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/company/${companyId}/multiple-employers`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ allow_multiple_employers: !currentValue })
      });
      if (res.ok) {
        toast.success(!currentValue ? "Multiple employers enabled" : "Multiple employers disabled");
        refreshCompanies();
      }
    } catch (e) { toast.error("Failed to update setting"); }
  };

  const handleSetCustomEnterprise = async () => {
    if (!selectedCompany) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/company/${selectedCompany.company_id}/custom-enterprise`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(enterpriseData)
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Custom Enterprise pricing set: ${enterpriseData.price} SAR/mo for ${enterpriseData.max_employees} employees`);
        if (data.stripe_price_id) {
          toast.info(`Stripe Price ID: ${data.stripe_price_id}`);
        }
        setShowEnterpriseDialog(false);
        refreshCompanies();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to set custom pricing");
      }
    } catch (e) { toast.error("Failed to set custom pricing"); }
  };

  const handleRemoveCustomEnterprise = async (companyId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/company/${companyId}/custom-enterprise`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        toast.success("Custom enterprise pricing removed");
        refreshCompanies();
      }
    } catch (e) { toast.error("Failed to remove custom pricing"); }
  };

  const handleGenerateCheckoutLink = async (company) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/company/${company.company_id}/checkout-link`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        // Try to copy to clipboard
        let copied = false;
        try {
          await navigator.clipboard.writeText(data.checkout_url);
          copied = true;
        } catch (clipErr) {
          console.log("Clipboard not available, opening URL in toast");
        }
        
        toast.success(
          <div>
            <p className="font-semibold">Checkout link generated!</p>
            <p className="text-xs">{data.price} SAR/mo for {data.max_employees} employees</p>
            {copied ? (
              <p className="text-xs text-green-600 mt-1">Link copied to clipboard!</p>
            ) : (
              <p className="text-xs text-blue-600 mt-1 break-all">{data.checkout_url.substring(0, 50)}...</p>
            )}
          </div>,
          { duration: 5000 }
        );
        refreshCompanies();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to generate checkout link");
      }
    } catch (e) { 
      console.error("Checkout error:", e);
      toast.error("Failed to generate checkout link"); 
    }
  };

  const handleToggleAdmin = async (userId, currentStatus, userName) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/user/${userId}/admin`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_admin: !currentStatus })
      });
      if (res.ok) {
        toast.success(currentStatus ? "Admin access revoked" : "Admin access granted");
        const usersRes = await fetch(`${API_URL}/api/admin/users`, { credentials: 'include' });
        if (usersRes.ok) setUsers(await usersRes.json());
      }
    } catch (e) { toast.error(e.message || "Failed to update"); }
  };
const openDeleteDialog = (target) => {
  setDeleteTarget(target);
  setDeleteMode("soft");
  setDeleteConfirmText("");
  setShowDeleteDialog(true);
};

const handleConfirmDelete = async () => {
  if (!deleteTarget) return;

  const requiredText =
    deleteTarget.type === "company"
      ? deleteTarget.name
      : deleteTarget.email;

  if (deleteConfirmText.trim() !== requiredText) {
    toast.error("Confirmation text does not match.");
    return;
  }

  setDeleting(true);
  try {
    const endpoint =
      deleteTarget.type === "company"
        ? `${API_URL}/api/admin/company/${deleteTarget.id}?mode=${deleteMode}`
        : `${API_URL}/api/admin/user/${deleteTarget.id}?mode=${deleteMode}`;

    const res = await fetch(endpoint, {
      method: "DELETE",
      credentials: "include",
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Delete failed");
      return;
    }

    toast.success("Deleted successfully");
    setShowDeleteDialog(false);

    // Refresh lists
    const [usersRes, companiesRes] = await Promise.all([
      fetch(`${API_URL}/api/admin/users`, { credentials: "include" }),
      fetch(`${API_URL}/api/admin/companies`, { credentials: "include" }),
    ]);
    if (usersRes.ok) setUsers(await usersRes.json());
    if (companiesRes.ok) setCompanies(await companiesRes.json());
  } catch (e) {
    toast.error("Delete failed");
  } finally {
    setDeleting(false);
  }
};
  
  // Pricing functions
  const fetchPricingPlans = async () => {
    setPricingLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/pricing`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPricingPlans(data.plans || {});
      }
    } catch (e) { toast.error("Failed to fetch pricing plans"); }
    finally { setPricingLoading(false); }
  };

  const handleSavePlan = async (planId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/pricing/${planId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editedPlanData)
      });
      if (res.ok) {
        toast.success(`Plan "${planId}" updated successfully`);
        setEditingPlan(null);
        setEditedPlanData({});
        fetchPricingPlans();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to update plan");
      }
    } catch (e) { toast.error("Failed to update plan"); }
  };

  const handleDeletePlan = async (planId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/pricing/${planId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        toast.success(`Plan "${planId}" deleted`);
        fetchPricingPlans();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to delete plan");
      }
    } catch (e) { toast.error("Failed to delete plan"); }
  };

  const startEditingPlan = (planId, plan) => {
    setEditingPlan(planId);
    setEditedPlanData({
      name: plan.name,
      price: plan.price,
      max_employees: plan.max_employees,
      description: plan.description || ""
    });
  };

  const filteredUsers = users.filter(u => 
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredCompanies = companies.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.owner_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className={`min-h-screen bg-background`} dir={isRTL ? 'rtl' : 'ltr'}>
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="w-8 h-8 rounded-full bg-destructive flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-xl">{t('adminDashboard')}</span>
          </div>
          <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {/* Language Selector */}
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-[130px]">
                <Globe className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {supportedLanguages.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.nativeName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="ghost" onClick={() => navigate('/')} className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
              <LogOut className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />{t('exitAdmin')}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Metrics */}
        {metrics && (
          <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 ${isRTL ? 'direction-rtl' : ''}`}>
            <MetricCard title={t('totalUsers')} value={metrics.total_users} icon={Users} color="text-blue-600" />
            <MetricCard title={t('companiesCount')} value={metrics.total_companies} icon={Building2} color="text-purple-600" />
            <MetricCard title={t('activeSubscriptions')} value={metrics.subscriptions.active} icon={CheckCircle} color="text-green-600" />
            <MetricCard title={t('trialUsers')} value={metrics.subscriptions.trial} icon={Clock} color="text-yellow-600" />
          </div>
        )}

        <Tabs defaultValue="companies" className="space-y-6" onValueChange={(value) => {
          if (value === "pricing") fetchPricingPlans();
        }}>
          <TabsList className={isRTL ? 'flex-row-reverse' : ''}>
            <TabsTrigger value="companies" className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}><Building2 className={`w-4 h-4 ${isRTL ? 'ml-1' : 'mr-1'}`} />{t('companiesTab')}</TabsTrigger>
            <TabsTrigger value="users" className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}><Users className={`w-4 h-4 ${isRTL ? 'ml-1' : 'mr-1'}`} />{t('usersTab')}</TabsTrigger>
            <TabsTrigger value="transactions" className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}><CreditCard className={`w-4 h-4 ${isRTL ? 'ml-1' : 'mr-1'}`} />{t('transactionsTab')}</TabsTrigger>
            <TabsTrigger value="pricing" data-testid="pricing-tab" className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}><DollarSign className={`w-4 h-4 ${isRTL ? 'ml-1' : 'mr-1'}`} />{t('pricingTab')}</TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-4 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
              <Input 
                placeholder={t('search') || "Search..."} 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)}
                className={isRTL ? 'pr-10' : 'pl-10'}
              />
            </div>
          </div>

          <TabsContent value="companies">
            <Card>
              <CardHeader>
                <CardTitle>{t('companiesTab')} ({filteredCompanies.length})</CardTitle>
                <CardDescription>{t('manageCompanySubscriptions')}</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('company')}</TableHead>
                      <TableHead>{t('owner')}</TableHead>
                      <TableHead>{t('employeesCount')}</TableHead>
                      <TableHead>{t('status')}</TableHead>
                      <TableHead>{t('plan')}</TableHead>
                      <TableHead>{t('trialDaysColumn')}</TableHead>
                      <TableHead>{t('customEnterprise')}</TableHead>
                      <TableHead>{t('multiEmployer')}</TableHead>
                      <TableHead>{t('actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCompanies.map((company) => (
                      <TableRow key={company.company_id}>
                        <TableCell className="font-medium">
                          {company.name}
                          {company.is_admin_company && <Badge className={`${isRTL ? 'mr-2' : 'ml-2'} bg-purple-100 text-purple-800`}>{t('admin')}</Badge>}
                        </TableCell>
                        <TableCell>{company.owner_email}</TableCell>
                        <TableCell>{company.employee_count}</TableCell>
                        <TableCell><StatusBadge status={company.subscription_status} /></TableCell>
                        <TableCell>
                          {company.is_admin_company ? (
                            <Badge className="bg-purple-100 text-purple-800">{t('admin')}</Badge>
                          ) : (
                            company.subscription_plan || "-"
                          )}
                        </TableCell>
                        <TableCell>
                          {company.is_admin_company ? (
                            <Badge className="bg-green-100 text-green-800">{t('unrestricted')}</Badge>
                          ) : company.subscription_status === "active" ? (
                            <Badge className="bg-green-100 text-green-800">{t('active')}</Badge>
                          ) : company.subscription_status === "canceled" ? (
                            <Badge className="bg-red-100 text-red-800">{t('canceled')}</Badge>
                          ) : company.subscription_status === "past_due" ? (
                            <Badge className="bg-orange-100 text-orange-800">{t('pastDue')}</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-yellow-50 text-yellow-800">
                              {company.trial_days || 10} {t('days')}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {company.is_admin_company ? (
                            <Badge className="bg-gray-100 text-gray-600">{t('na')}</Badge>
                          ) : company.custom_enterprise_price ? (
                            <div className="flex items-center gap-2">
                              <Badge className="bg-purple-100 text-purple-800">
                                {company.custom_enterprise_price} SAR / {company.custom_enterprise_max_employees} emp
                              </Badge>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="h-6 px-2 text-xs"
                                onClick={() => handleGenerateCheckoutLink(company)}
                                title="Generate & Copy Checkout Link"
                              >
                                <CreditCard className="w-3 h-3 mr-1" />Link
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-6 w-6 p-0 text-red-500"
                                onClick={() => handleRemoveCustomEnterprise(company.company_id)}
                                title={t('removeCustomPricing')}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-xs"
                              onClick={() => {
                                setSelectedCompany(company);
                                setEnterpriseData({ price: 1000, max_employees: 200, create_stripe_price: true });
                                setShowEnterpriseDialog(true);
                              }}
                              data-testid={`set-enterprise-${company.company_id}`}
                            >
                              <DollarSign className={`w-3 h-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />{t('set')}
                            </Button>
                          )}
                        </TableCell>
                        <TableCell>
                          {!company.is_admin_company && (
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={company.allow_multiple_employers || false}
                                onCheckedChange={() => handleToggleMultipleEmployers(company.company_id, company.allow_multiple_employers)}
                                data-testid={`multi-employer-toggle-${company.company_id}`}
                              />
                              {company.allow_multiple_employers && (
                                <UserPlus className="w-4 h-4 text-green-600" />
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {!company.is_admin_company && (
                            <div className="flex gap-1 flex-wrap">
                              <Button size="sm" variant="outline" onClick={() => { setSelectedCompany(company); setEditData({ subscription_status: company.subscription_status, subscription_plan: company.subscription_plan, trial_days: company.trial_days || 10, timezone_offset: company.timezone_offset ?? 3 }); setShowEditDialog(true); }}>{t('edit')}</Button>
                              {company.subscription_status === "trial" && (
                                <>
                                  <Button size="sm" variant="outline" onClick={() => { setSelectedCompany(company); setTrialDaysInput(10); setShowTrialDialog(true); }} title={t('manageTrial')}><Clock className="w-4 h-4" /></Button>
                                  <Button size="sm" variant="ghost" onClick={() => handleResetTrial(company.company_id)} title={t('resetTrial')}><RefreshCw className="w-4 h-4" /></Button>
                                </>
                              )}
                              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleClearAllShifts(company.company_id)} title={t('clearShifts')}><Trash2 className="w-4 h-4" /></Button>
                              <Button
  size="sm"
  variant="ghost"
  className="text-destructive"
  onClick={() =>
    openDeleteDialog({
      type: "company",
      id: company.company_id,
      name: company.name,
    })
  }
  title="Delete Company"
>
  <Trash2 className="w-4 h-4" />
</Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>{t('usersTab')} ({filteredUsers.length})</CardTitle>
                <CardDescription>{t('manageUsers')}</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('email')}</TableHead>
                      <TableHead>{t('name')}</TableHead>
                      <TableHead>{t('role')}</TableHead>
                      <TableHead>{t('company')}</TableHead>
                      <TableHead>{t('status')}</TableHead>
                      <TableHead>{t('admin')}</TableHead>
                      <TableHead>{t('actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
  {filteredUsers.map((u) => (
    <TableRow key={u.user_id}>
      <TableCell className="font-medium">{u.email}</TableCell>
      <TableCell>{u.display_name || u.name}</TableCell>
      <TableCell><Badge variant="outline">{u.role || "None"}</Badge></TableCell>
      <TableCell>{u.company_name || "-"}</TableCell>
      <TableCell><StatusBadge status={u.subscription_status || "none"} /></TableCell>
      <TableCell>{u.is_admin ? <Badge className="bg-red-100 text-red-800">{t("admin")}</Badge> : "-"}</TableCell>

      {/* ACTIONS */}
      <TableCell>
        <div className="flex gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                size="sm"
                variant={u.is_admin ? "destructive" : "outline"}
                data-testid={`admin-toggle-${u.user_id}`}
              >
                {u.is_admin ? t("revokeAdmin") : t("makeAdmin")}
              </Button>
            </AlertDialogTrigger>

            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {u.is_admin ? t("confirmRevokeAdmin") : t("confirmMakeAdmin")}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {u.is_admin ? t("confirmRevokeAdminDesc") : t("confirmMakeAdminDesc")}
                </AlertDialogDescription>
              </AlertDialogHeader>

              <AlertDialogFooter>
                <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => handleToggleAdmin(u.user_id, u.is_admin, u.display_name || u.name)}
                  className={u.is_admin ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
                >
                  {u.is_admin ? t("revokeAdminBtn") : t("grantAdmin")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Button
            size="sm"
            variant="ghost"
            className="text-destructive"
            onClick={() =>
              openDeleteDialog({
                type: "user",
                id: u.user_id,
                email: u.email,
                name: u.display_name || u.name,
              })
            }
            title="Delete User"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  ))}
</TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="transactions">
            <Card>
              <CardHeader>
                <CardTitle>{t('transactionsTab')} ({transactions.length})</CardTitle>
                <CardDescription>{t('paymentHistory')}</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('date')}</TableHead>
                      <TableHead>{t('company')}</TableHead>
                      <TableHead>{t('plan')}</TableHead>
                      <TableHead>{t('amount')}</TableHead>
                      <TableHead>{t('status')}</TableHead>
                      <TableHead>{t('actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((txn) => (
                      <TableRow key={txn.transaction_id}>
                        <TableCell>{new Date(txn.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>{txn.company_name}</TableCell>
                        <TableCell><Badge variant="outline">{txn.plan}</Badge></TableCell>
                        <TableCell>{txn.amount} {txn.currency?.toUpperCase()}</TableCell>
                        <TableCell>
                          <Badge className={
                            txn.payment_status === "paid" ? "bg-green-100 text-green-800" : 
                            txn.payment_status === "failed" ? "bg-red-100 text-red-800" :
                            txn.payment_status === "canceled" ? "bg-gray-100 text-gray-800" :
                            txn.payment_status === "refunded" ? "bg-purple-100 text-purple-800" :
                            "bg-yellow-100 text-yellow-800"
                          }>
                            {txn.payment_status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Select 
                              defaultValue={txn.payment_status}
                              onValueChange={(value) => handleUpdateTransaction(txn.transaction_id, value)}
                            >
                              <SelectTrigger className="w-28 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">{t('pending')}</SelectItem>
                                <SelectItem value="paid">{t('paid')}</SelectItem>
                                <SelectItem value="failed">{t('denied')}</SelectItem>
                                <SelectItem value="canceled">{t('canceled')}</SelectItem>
                                <SelectItem value="refunded">{t('refunded') || 'Refunded'}</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button size="sm" variant="ghost" className="text-destructive h-8" onClick={() => handleDeleteTransaction(txn.transaction_id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {transactions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">{t('noTransactions')}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="pricing" data-testid="pricing-content">
            <Card>
              <CardHeader>
                <CardTitle>{t('pricingPlans')}</CardTitle>
                <CardDescription>{t('managePricing')}</CardDescription>
              </CardHeader>
              <CardContent>
                {pricingLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {Object.entries(pricingPlans).map(([planId, plan]) => (
                      <div key={planId} className="border rounded-lg p-4" data-testid={`pricing-plan-${planId}`}>
                        {editingPlan === planId ? (
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div className="space-y-2">
                                <Label>{t('planName')}</Label>
                                <Input 
                                  value={editedPlanData.name || ""} 
                                  onChange={(e) => setEditedPlanData({...editedPlanData, name: e.target.value})}
                                  data-testid={`edit-plan-name-${planId}`}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>{t('priceSAR')}</Label>
                                <Input 
                                  type="number" 
                                  value={editedPlanData.price || 0} 
                                  onChange={(e) => setEditedPlanData({...editedPlanData, price: parseFloat(e.target.value) || 0})}
                                  data-testid={`edit-plan-price-${planId}`}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>{t('maxEmployees')}</Label>
                                <Input 
                                  type="number" 
                                  value={editedPlanData.max_employees || 0} 
                                  onChange={(e) => setEditedPlanData({...editedPlanData, max_employees: parseInt(e.target.value) || 0})}
                                  data-testid={`edit-plan-employees-${planId}`}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>{t('description')}</Label>
                                <Input 
                                  value={editedPlanData.description || ""} 
                                  onChange={(e) => setEditedPlanData({...editedPlanData, description: e.target.value})}
                                  placeholder={t('description')}
                                />
                              </div>
                            </div>
                            <div className={`flex gap-2 ${isRTL ? 'justify-start' : 'justify-end'}`}>
                              <Button variant="outline" size="sm" onClick={() => { setEditingPlan(null); setEditedPlanData({}); }}>
                                {t('cancel')}
                              </Button>
                              <Button size="sm" onClick={() => handleSavePlan(planId)} data-testid={`save-plan-${planId}`}>
                                <Save className={`w-4 h-4 ${isRTL ? 'ml-1' : 'mr-1'}`} />{t('save')}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <div className={isRTL ? 'text-right' : ''}>
                                <h3 className="font-semibold text-lg">{plan.name}</h3>
                                <p className="text-sm text-muted-foreground">{planId}</p>
                              </div>
                              <Badge variant="outline" className={isRTL ? 'mr-4' : 'ml-4'}>
                                {plan.price > 0 ? `${plan.price} SAR/${t('month')}` : t('freeTrial')}
                              </Badge>
                              <Badge variant="secondary">
                                {plan.max_employees === 999999 ? t('planEnterpriseTeam') : `${plan.max_employees} ${t('employees')}`}
                              </Badge>
                              {plan.is_trial && <Badge className="bg-yellow-100 text-yellow-800">{t('trial')}</Badge>}
                              {plan.contact && <Badge className="bg-blue-100 text-blue-800">{t('contactUs')}</Badge>}
                            </div>
                            <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <Button variant="outline" size="sm" onClick={() => startEditingPlan(planId, plan)} data-testid={`edit-btn-${planId}`}>
                                <Edit2 className={`w-4 h-4 ${isRTL ? 'ml-1' : 'mr-1'}`} />{t('edit')}
                              </Button>
                              {!["starter", "enterprise"].includes(planId) && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="ghost" size="sm" className="text-destructive" data-testid={`delete-btn-${planId}`}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>{t('deletePlan')} "{plan.name}"?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        {t('deletePlanConfirm')}
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => handleDeletePlan(planId)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                        {t('deletePlan')}
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {Object.keys(pricingPlans).length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        No pricing plans found. Click the button below to load defaults.
                        <Button variant="outline" className="mt-4" onClick={fetchPricingPlans}>
                          Load Default Plans
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Edit Company Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Company: {selectedCompany?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Subscription Status</label>
              <Select value={editData.subscription_status} onValueChange={(v) => setEditData({ ...editData, subscription_status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="canceled">Canceled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Subscription Plan</label>
              <Select value={editData.subscription_plan || ""} onValueChange={(v) => setEditData({ ...editData, subscription_plan: v })}>
                <SelectTrigger><SelectValue placeholder="Select plan" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter (Trial)</SelectItem>
                  <SelectItem value="growth">Growth</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Trial Days</label>
              <Input type="number" value={editData.trial_days || 10} onChange={(e) => setEditData({ ...editData, trial_days: parseInt(e.target.value) || 10 })} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Company Timezone</label>
              <Select value={String(editData.timezone_offset ?? 3)} onValueChange={(v) => setEditData({ ...editData, timezone_offset: Number(v) })}>
                <SelectTrigger><SelectValue placeholder="Select timezone" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="-12">UTC-12 (Baker Island)</SelectItem>
                  <SelectItem value="-11">UTC-11 (Samoa)</SelectItem>
                  <SelectItem value="-10">UTC-10 (Hawaii)</SelectItem>
                  <SelectItem value="-9">UTC-9 (Alaska)</SelectItem>
                  <SelectItem value="-8">UTC-8 (Pacific Time)</SelectItem>
                  <SelectItem value="-7">UTC-7 (Mountain Time)</SelectItem>
                  <SelectItem value="-6">UTC-6 (Central Time)</SelectItem>
                  <SelectItem value="-5">UTC-5 (Eastern Time)</SelectItem>
                  <SelectItem value="-4">UTC-4 (Atlantic)</SelectItem>
                  <SelectItem value="-3">UTC-3 (Brazil)</SelectItem>
                  <SelectItem value="-2">UTC-2</SelectItem>
                  <SelectItem value="-1">UTC-1 (Azores)</SelectItem>
                  <SelectItem value="0">UTC+0 (London)</SelectItem>
                  <SelectItem value="1">UTC+1 (Paris)</SelectItem>
                  <SelectItem value="2">UTC+2 (Cairo)</SelectItem>
                  <SelectItem value="3">UTC+3 (Riyadh, Moscow)</SelectItem>
                  <SelectItem value="4">UTC+4 (Dubai)</SelectItem>
                  <SelectItem value="5">UTC+5 (Karachi)</SelectItem>
                  <SelectItem value="6">UTC+6 (Dhaka)</SelectItem>
                  <SelectItem value="7">UTC+7 (Bangkok)</SelectItem>
                  <SelectItem value="8">UTC+8 (Singapore, Beijing)</SelectItem>
                  <SelectItem value="9">UTC+9 (Tokyo)</SelectItem>
                  <SelectItem value="10">UTC+10 (Sydney)</SelectItem>
                  <SelectItem value="11">UTC+11 (Solomon Islands)</SelectItem>
                  <SelectItem value="12">UTC+12 (Auckland)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Used for late check-in detection</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateSubscription}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trial Days Dialog */}
      <Dialog open={showTrialDialog} onOpenChange={setShowTrialDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Trial: {selectedCompany?.name}</DialogTitle>
            <DialogDescription>Add or set custom trial days</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-4">
              <div className="flex-1 space-y-2">
                <Label>Days</Label>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setTrialDaysInput(Math.max(1, trialDaysInput - 5))}><Minus className="w-4 h-4" /></Button>
                  <Input type="number" value={trialDaysInput} onChange={(e) => setTrialDaysInput(parseInt(e.target.value) || 1)} className="w-20 text-center" />
                  <Button variant="outline" size="sm" onClick={() => setTrialDaysInput(trialDaysInput + 5)}><Plus className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => handleExtendTrial(selectedCompany?.company_id, trialDaysInput)}>
                <Plus className="w-4 h-4 mr-1" />Add {trialDaysInput} Days
              </Button>
              <Button className="flex-1" onClick={() => handleSetTrialDays(selectedCompany?.company_id, trialDaysInput)}>
                Set to {trialDaysInput} Days
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Enterprise Pricing Dialog */}
      <Dialog open={showEnterpriseDialog} onOpenChange={setShowEnterpriseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Custom Enterprise Pricing</DialogTitle>
            <DialogDescription>
              Company: <strong>{selectedCompany?.name}</strong>
              <br />
              Set a custom price and employee limit for this company's Enterprise plan.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Monthly Price (SAR)</Label>
              <Input 
                type="number" 
                value={enterpriseData.price} 
                onChange={(e) => setEnterpriseData({...enterpriseData, price: parseFloat(e.target.value) || 0})}
                placeholder="e.g., 1500"
                data-testid="enterprise-price-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Max Employees</Label>
              <Input 
                type="number" 
                value={enterpriseData.max_employees} 
                onChange={(e) => setEnterpriseData({...enterpriseData, max_employees: parseInt(e.target.value) || 0})}
                placeholder="e.g., 200"
                data-testid="enterprise-employees-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                checked={enterpriseData.create_stripe_price} 
                onCheckedChange={(checked) => setEnterpriseData({...enterpriseData, create_stripe_price: checked})}
                id="create-stripe"
              />
              <Label htmlFor="create-stripe" className="text-sm">
                Create Stripe Price automatically
              </Label>
            </div>
            <div className="text-xs text-muted-foreground bg-muted p-3 rounded-lg">
              This will create a custom Stripe Product and Price for this company.
              They can then subscribe using their unique checkout link.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEnterpriseDialog(false)}>Cancel</Button>
            <Button onClick={handleSetCustomEnterprise} data-testid="save-enterprise-btn">
              <DollarSign className="w-4 h-4 mr-1" />Set Custom Pricing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
  <DialogContent>
    <DialogHeader>
      <DialogTitle>
        {deleteTarget?.type === "company" ? "Delete Company" : "Delete User"}
      </DialogTitle>
    </DialogHeader>

    <div className="space-y-4 py-2">
      <div className="space-y-2">
        <Label>Delete mode</Label>
        <Select value={deleteMode} onValueChange={setDeleteMode}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="soft">Soft delete</SelectItem>
            <SelectItem value="hard">Hard delete</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>
          Type to confirm:{" "}
          <span className="font-semibold">
            {deleteTarget?.type === "company"
              ? deleteTarget?.name
              : deleteTarget?.email}
          </span>
        </Label>
        <Input
          value={deleteConfirmText}
          onChange={(e) => setDeleteConfirmText(e.target.value)}
        />
      </div>
    </div>

    <DialogFooter>
      <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
        Cancel
      </Button>
      <Button
        className="bg-destructive text-destructive-foreground"
        onClick={handleConfirmDelete}
        disabled={deleting}
      >
        {deleting ? "Deleting..." : "Delete"}
      </Button>
    </DialogFooter>
  </DialogContent>
</Dialog>
    </div>
  );
};

export default AdminDashboard;
